import { IAssetV2, IMenuDataResponseV2, IMenuItemResponseV2, IMenuSectionResponse } from 'src/api/getMenu/types';
import { IMenu, IMenuItem, ISection } from 'src/types/menu.types';
import { getDayIndex } from 'src/helpers/day/day';
import { IAsset } from 'src/helpers/menus/menus.types';

export const findImageUrl = (assets: (IAsset | IAssetV2)[]): string | null => {
  const imageRegex = /\.(png|jpeg|jpg)$/i;
  const assetData = assets?.find((asset: IAsset | IAssetV2) => imageRegex.test(asset.name));

  if (!assetData) return null;

  if ('asset_url' in assetData) return assetData.asset_url; // for IAssetV2

  return assetData?.asset_variants[0]?.url; // for IAsset
};

export const findPDFAsset = (assets: (IAsset | IAssetV2)[]): IAsset | IAssetV2 | null => {
  const pdfRegex = /\.(pdf)$/i;
  return assets?.find((asset: IAsset | IAssetV2) => pdfRegex.test(asset.name)) || null;
};

const getMenuModifiers = (menuItems: IMenuItemResponseV2[]) =>
  menuItems.filter((item: IMenuItemResponseV2) => item.type === 'MODIFIER');

const getMenuSections = (sections: IMenuSectionResponse[], menuItems: IMenuItemResponseV2[]) =>
  sections.map((section: IMenuSectionResponse) => ({
    name: section.name,
    sectionId: section.section_id,
    description: section.description,
    displayOrder: section.display_order,
    items: formatMenuItems(
      section.items.flatMap((item: string) =>
        menuItems.filter(
          (singleItem: IMenuItemResponseV2) => item === singleItem.item_id && singleItem.type !== 'MODIFIER',
        ),
      ),
    ),
    parentSectionId: section.parent_section_id,
  }));

const getUncategorised = (sections: ISection[], menuItems: IMenuItemResponseV2[]) =>
  menuItems.filter(
    (item: IMenuItemResponseV2) =>
      item.type !== 'MODIFIER' &&
      sections.every((section) => section.items.every((sectionItem) => sectionItem.itemId !== item.item_id)),
  );

export const formatMenuItems = (menuItems: IMenuItemResponseV2[]): IMenuItem[] => {
  return menuItems.map((item: IMenuItemResponseV2) => ({
    itemId: item.item_id,
    name: item.name,
    consumerName: item.consumer_name,
    type: item.type,
    image: item.asset_url,
    parentItemId: item.parent_item_id,
    displayOrder: item.display_order,
    description: null,
    prices: {
      defaultPrice: undefined,
      taxRate: undefined,
      priceBands: [],
    },
    tax_rate: null,
    new_prices: [],
    recipeId: null,
    optionGroup: [],
    deposit_return_scheme: null,
  }));
};

export const formatMenu = (data: IMenuDataResponseV2): IMenu => {
  const menu = {
    name: data.name,
    menuId: data.menu_id,
    consumerName: data.consumer_name,
    image: findImageUrl(data.assets),
    allergensPDF: findPDFAsset(data.assets),
    schedules: data.schedules
      .sort((a, b) => {
        return getDayIndex(a.day_of_week) - getDayIndex(b.day_of_week);
      })
      .map((item) => ({
        dayOfWeek: getDayIndex(item.day_of_week),
        startTime: item.start_time,
        endTime: item.end_time,
      })),
    publication: {
      publicationStatus: data.publication.status,
      publicationDate: data.publication.publication_date ? new Date(data.publication.publication_date) : null,
      aggregators: data.publication.channels.map((aggregator) => ({
        aggregatorId: aggregator.external_system_id,
        publicationStatus: aggregator.status,
        publicationDate: new Date(aggregator.publication_date),
        venues: aggregator.venues.map((venue) => ({
          venueId: venue.venue_id,
          publicationDate: new Date(venue.publication_date),
          publicationStatus: venue.status,
          priceSchemeName: venue.price_scheme_name,
          priceSchemeId: venue.price_scheme_id,
        })),
      })),
    },
    created: new Date(data.create_date),
    linkedVenueIds: new Set(data.linked_venues),
    mealtimes: data.mealtimes,
    ranges: data.ranges || [],
  }; // shared values for V1 and V2

  const modifiers = getMenuModifiers(data.items);
  const sections = getMenuSections(data.sections, data.items);
  const uncategorised = getUncategorised(sections, data.items);

  return {
    ...menu,
    modifiers: formatMenuItems(modifiers),
    sections,
    uncategorised: formatMenuItems(uncategorised),
    scheduledPublications: [
      {
        publicationDate: data.scheduled_publication_date ? new Date(data.scheduled_publication_date) : null,
      },
    ],
  };
};
